import React, { useState } from 'react';
import { RALModal } from '../../modals';

// Helper to check if finish requires RAL code
const isSpecialFinish = (code) => code === 'V1' || code === 'V2';

// LED Profile configuration rules
const LED_CONFIGS = {
    aluminum: {
        code: 'N6-SP*',
        baseCode: 'N6-SP',
        name: 'Profilo Segnapassi',
        image: '/images/accessories/LED/SEL-N6-SP.png',
        material: 'aluminum',
        description: 'Profilo segnapassi in alluminio',
        supportsSupplyType: true // Can be barre or misura
    },
    plasticStandard: {
        code: 'FL10B',
        baseCode: 'FL10B',
        name: 'Profilo Plastico',
        material: 'plastic',
        description: 'Profilo plastico - Aggiunge automaticamente di pari quantità del profilo segnapassi a barre',
        supportsSupplyType: false,
        autoAddRule: 'match_aluminum_barre' // Auto-add aluminum profile with barre supply
    },
    plasticCustom: {
        code: 'FL10B-T',
        baseCode: 'FL10B-T',
        name: 'Profilo Plastico a Misura',
        material: 'plastic',
        description: 'Profilo plastico - Aggiunge automaticamente di pari quantità del profilo segnapassi a misura',
        supportsSupplyType: false,
        autoAddRule: 'match_aluminum_misura' // Auto-add aluminum profile with misura supply
    }
};

// Available finishes for aluminum profiles
const FINISH_OPTIONS = [
    { code: 'A', name: 'Argento', suffix: 'A', color: '#C0C0C0' },
    { code: 'B', name: 'RAL9010 Opaco', suffix: 'B', color: '#FFFFFF' },
    { code: 'G', name: 'Grezzo', suffix: 'G', color: '#E0E0E0' },
    { code: 'N', name: 'RAL9005 Opaco', suffix: 'N', color: '#000000' },
    { code: 'V1', name: 'Verniciato Speciale 1', suffix: 'V1', color: '#D3D3D3' },
    { code: 'V2', name: 'Verniciato Speciale 2', suffix: 'V2', color: '#B8B8B8' }
];

// Supply type options
const SUPPLY_TYPE_OPTIONS = [
    { value: '3m', label: 'Barre da 3 Metri', icon: '3M' },
    { value: '6m', label: 'Barre da 6 Metri', icon: '6M' },
    { value: 'misura', label: 'Tagliato a Misura', icon: 'M' }
];

export const LedProfileSelector = ({ onSelect, initialValues = {} }) => {
    // State management
    const [step, setStep] = useState(1);
    const [selectedProfile, setSelectedProfile] = useState(initialValues?.selectedProfile || null);
    const [supplyType, setSupplyType] = useState(initialValues?.supplyType || '');
    const [finish, setFinish] = useState(initialValues?.finish || '');
    const [ralCode, setRalCode] = useState(initialValues?.ralCode || null);
    const [quantity, setQuantity] = useState(initialValues?.quantity || 1);
    const [customBars, setCustomBars] = useState(initialValues?.customBars || []);

    // RAL Modal state
    const [showRALModal, setShowRALModal] = useState(false);
    const [pendingFinishCode, setPendingFinishCode] = useState(null);

    // Step 1: Profile selection (only aluminum, plastic is auto-added)
    const handleProfileSelection = () => {
        const profile = LED_CONFIGS.aluminum;
        setSelectedProfile(profile);
        setStep(2); // Go to supply type selection
    };

    // Step 2: Supply type selection (only for aluminum)
    const handleSupplyTypeSelect = (supplyTypeValue) => {
        setSupplyType(supplyTypeValue);

        if (supplyTypeValue === 'misura') {
            setStep(5); // Go to custom bars input
        } else {
            setStep(3); // Go to finish selection for 3m/6m
        }
    };

    // Step 3: Finish selection (only for aluminum)
    const handleFinishSelect = (finishCode) => {
        if (isSpecialFinish(finishCode)) {
            setPendingFinishCode(finishCode);
            setShowRALModal(true);
        } else {
            setFinish(finishCode);
            setRalCode(null);
            // For misura, go to final review (step 6), otherwise go to quantity (step 4)
            setStep(supplyType === 'misura' ? 6 : 4);
        }
    };

    // Handle RAL confirmation
    const handleRALConfirm = (code) => {
        setShowRALModal(false);
        setFinish(pendingFinishCode);
        setRalCode(code);
        setPendingFinishCode(null);
        setStep(supplyType === 'misura' ? 6 : 4);
    };

    // Handle RAL modal close
    const handleRALClose = () => {
        setShowRALModal(false);
        setPendingFinishCode(null);
    };

    // Step 4: Quantity configuration
    const handleQuantityChange = (e) => {
        const raw = e.target.value;
        if (raw === '') {
            setQuantity('');
            return;
        }
        const value = parseInt(raw) || 1;
        setQuantity(Math.max(1, value));
    };

    // Generate product codes based on selections
    const generateProductCodes = () => {
        const products = [];

        if (!selectedProfile) return products;

        // Generate aluminum profile code: N6-SP + finish suffix + optional L for misura
        const finishConfig = FINISH_OPTIONS.find(f => f.code === finish);
        let aluminumCode = `${selectedProfile.baseCode}${finishConfig?.suffix || ''}`;

        // Determine supply type label, length, and if it's barre or misura
        let supplyTypeLabel = '';
        let length = 0;
        const isBarre = supplyType === '3m' || supplyType === '6m';

        // Calculate quantity in METERS based on supply type
        let finalQuantity = quantity;

        if (supplyType === '3m') {
            supplyTypeLabel = 'Barre da 3 Metri';
            length = 3000; // in mm
            // quantity × 3 meters per bar
            finalQuantity = quantity * 3;
        } else if (supplyType === '6m') {
            supplyTypeLabel = 'Barre da 6 Metri';
            length = 6000; // in mm
            // quantity × 6 meters per bar
            finalQuantity = quantity * 6;
        } else if (supplyType === 'misura') {
            supplyTypeLabel = 'Tagliato a Misura';
            aluminumCode = aluminumCode + 'T'; // Add T suffix for misura (Tagliato)
            // Calculate total millimeters from custom bars, then convert to meters with 3 decimals
            if (customBars.length > 0) {
                const totalMm = customBars.reduce((sum, bar) => sum + ((bar.quantity || 0) * (bar.length || 0)), 0);
                finalQuantity = parseFloat((totalMm / 1000).toFixed(3));
            }
        }

        // Build description with length for barre
        const lengthInfo = length > 0 ? ` (${length}mm)` : '';

        // Add main aluminum profile
        const ralInfo = ralCode ? ` - ${ralCode}` : '';
        products.push({
            code: aluminumCode,
            quantity: finalQuantity,
            description: `${selectedProfile.name} - ${supplyTypeLabel}${lengthInfo} - ${finishConfig?.name || finish}${ralInfo}`,
            category: 'main',
            length: length || null,
            ralCode: ralCode || null
        });

        // Auto-add plastic profile based on supply type
        const plasticCode = isBarre ? 'FL10B' : 'FL10B-T';
        const plasticName = isBarre ? 'Profilo Plastico' : 'Profilo Plastico a Misura';

        products.push({
            code: plasticCode,
            quantity: finalQuantity, // Same quantity as aluminum
            description: `${plasticName}${lengthInfo} (Aggiunta automatica per segnapassi ${supplyTypeLabel})`,
            category: 'automatic',
            length: length || null
        });

        return products;
    };

    // Final submission
    const handleSubmit = () => {
        const productCodes = generateProductCodes();
        const ledProfileConfig = {
            selectedProfile,
            supplyType,
            finish,
            ralCode: ralCode || null,
            quantity,
            customBars: supplyType === 'misura' ? customBars : undefined,
            productCodes
        };

        onSelect(ledProfileConfig);
    };

    // Render steps
    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">Profilo Segnapassi</h2>
                        <p className="text-gray-600 mb-6">Il profilo plastico verrà aggiunto automaticamente in base al tipo di fornitura</p>

                        <div className="max-w-md mx-auto mb-6">
                            <button
                                onClick={handleProfileSelection}
                                className="w-full p-6 border-2 rounded-xl border-gray-200 bg-white hover:border-red-500 hover:shadow-lg transition-all"
                            >
                                <div className="flex flex-col items-center">
                                    {LED_CONFIGS.aluminum.image ? (
                                        <div className="w-full h-24 mb-3 flex items-center justify-center">
                                            <img
                                                src={LED_CONFIGS.aluminum.image}
                                                alt={LED_CONFIGS.aluminum.name}
                                                className="h-20 object-contain"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextElementSibling.style.display = 'flex';
                                                }}
                                            />
                                            <div className="w-20 h-20 hidden items-center justify-center bg-red-50 rounded-full">
                                                <span className="text-3xl font-bold text-red-600">N6</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="w-20 h-20 flex items-center justify-center mb-3 bg-red-50 rounded-full">
                                            <span className="text-3xl font-bold text-red-600">N6</span>
                                        </div>
                                    )}
                                    <span className="font-medium text-lg text-gray-700">Profilo Segnapassi</span>
                                    <span className="text-sm text-gray-600 mt-2">Alluminio N6-SP*</span>
                                    <span className="text-xs text-gray-500 mt-2">+ Profilo plastico automatico</span>
                                </div>
                            </button>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">Seleziona il Tipo di Fornitura</h2>
                        <p className="text-gray-600 mb-6">Scegli la lunghezza delle barre o tagliato a misura</p>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 max-w-3xl mx-auto">
                            {SUPPLY_TYPE_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => handleSupplyTypeSelect(option.value)}
                                    className={`p-4 border-2 rounded-lg ${supplyType === option.value ? 'bg-red-100 border-red-500' : 'border-gray-300 hover:border-red-300'}`}
                                >
                                    <div className="flex flex-col items-center">
                                        <div className="w-16 h-16 flex items-center justify-center mb-2 bg-red-50 rounded-full">
                                            <span className="text-lg font-bold">{option.icon}</span>
                                        </div>
                                        <span className="font-medium">{option.label}</span>
                                    </div>
                                </button>
                            ))}
                        </div>

                        <div className="flex justify-center space-x-4 mt-6">
                            <button
                                onClick={() => setStep(1)}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Indietro
                            </button>
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">Seleziona la Finitura</h2>
                        <p className="text-gray-600 mb-6">Scegli la finitura per il profilo segnapassi</p>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                            {FINISH_OPTIONS.map((finishOption) => (
                                <button
                                    key={finishOption.code}
                                    onClick={() => handleFinishSelect(finishOption.code)}
                                    className={`p-4 border rounded-lg ${finish === finishOption.code ? 'bg-red-100 border-red-500' : 'border-gray-300'}`}
                                >
                                    <div className="flex flex-col items-center">
                                        <div
                                            className="w-16 h-16 rounded-full mb-2 border border-gray-300"
                                            style={{ backgroundColor: finishOption.color }}
                                        ></div>
                                        <span className="font-medium">{finishOption.name}</span>
                                        <span className="text-sm text-gray-500">{finishOption.suffix}</span>
                                    </div>
                                </button>
                            ))}
                        </div>

                        <div className="flex justify-center space-x-4 mt-6">
                            <button
                                onClick={() => setStep(2)}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Indietro
                            </button>
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">Configura la Quantità</h2>

                        <div className="max-w-md mx-auto p-4 border-2 rounded-lg border-gray-200 mb-6">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Quantità:
                                </label>
                                <input
                                    type="number"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    min="1"
                                    value={quantity ?? ''}
                                    onChange={handleQuantityChange}
                                    className="w-full p-3 text-base border rounded-md text-center"
                                />
                            </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg mb-6">
                            <h3 className="font-medium mb-2">Riepilogo Prodotti</h3>
                            {generateProductCodes().map((product, index) => (
                                <div key={index} className={`text-sm ${product.category === 'automatic' ? 'text-orange-600' : ''}`}>
                                    <span className="font-medium">{product.code}</span> -
                                    Qty: {product.quantity} - {product.description}
                                    {product.category === 'automatic' && (
                                        <span className="ml-2 text-xs bg-orange-100 px-2 py-1 rounded">Auto</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-center space-x-4 mt-6">
                            <button
                                onClick={() => setStep(3)}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Indietro
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={!quantity || quantity === '' || parseInt(quantity) < 1}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Aggiungi al Carrello
                            </button>
                        </div>
                    </div>
                );

            case 5:
                // Custom bars input for "Tagliato a Misura"
                return (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold text-center mb-2">
                            Tagliato a Misura
                        </h2>
                        <p className="text-gray-600 text-center mb-6">
                            Aggiungi le barre di cui hai bisogno con il pulsante "+"
                        </p>
                        <div className="space-y-6">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-center mb-3">
                                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h4 className="font-medium text-red-900">Esempio</h4>
                                </div>
                                <p className="text-sm text-red-800">
                                    3 barre da 2900mm, 5 barre da 5500mm, 1 barra da 1000mm
                                </p>
                            </div>

                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-center mb-2">
                                    <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <h4 className="font-medium text-yellow-900">Restrizioni</h4>
                                </div>
                                <p className="text-sm text-yellow-800">
                                    Non puoi inserire esattamente 3000mm, 6000mm o valori maggiori di 6000mm
                                </p>
                            </div>

                            {/* Bar rows */}
                            <div className="space-y-3">
                                {customBars.map((bar, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <div className="flex-1">
                                            <input
                                                type="number"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                min="1"
                                                max="100"
                                                value={bar.quantity === '' ? '' : (bar.quantity || '')}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    const newBars = [...customBars];
                                                    newBars[index].quantity = raw === '' ? '' : Math.max(1, parseInt(raw) || 1);
                                                    setCustomBars(newBars);
                                                }}
                                                className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                                placeholder="N° barre"
                                            />
                                        </div>
                                        <span className="text-gray-500 font-medium">barre da</span>
                                        <div className="flex-1">
                                            <input
                                                type="number"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                min="1"
                                                max="5999"
                                                value={bar.length || ''}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    const newBars = [...customBars];
                                                    if (raw === '') {
                                                        newBars[index].length = '';
                                                        setCustomBars(newBars);
                                                        return;
                                                    }
                                                    const lengthValue = parseInt(raw) || 0;

                                                    // Validate: cannot be exactly 3000, 6000, or greater than 6000
                                                    if (lengthValue === 3000 || lengthValue === 6000 || lengthValue > 6000) {
                                                        e.target.classList.add('border-red-500', 'bg-red-50');
                                                        setTimeout(() => {
                                                            e.target.classList.remove('border-red-500', 'bg-red-50');
                                                        }, 1000);
                                                        return;
                                                    }

                                                    newBars[index].length = lengthValue;
                                                    setCustomBars(newBars);
                                                }}
                                                className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                                placeholder="Millimetri"
                                            />
                                        </div>
                                        <span className="text-gray-500 font-medium">mm</span>
                                        <button
                                            onClick={() => {
                                                const newBars = customBars.filter((_, i) => i !== index);
                                                setCustomBars(newBars);
                                            }}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Rimuovi"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Add bar button */}
                            <button
                                onClick={() => {
                                    const newBars = [...customBars, { quantity: 1, length: 0 }];
                                    setCustomBars(newBars);
                                }}
                                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-red-400 hover:text-red-600 transition-colors flex items-center justify-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Aggiungi Barra
                            </button>

                            {/* Summary */}
                            {customBars.length > 0 && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h4 className="font-medium text-gray-900 mb-2">Riepilogo:</h4>
                                    <div className="space-y-1 text-sm text-gray-700">
                                        {customBars.map((bar, idx) => (
                                            <div key={idx}>
                                                • {bar.quantity || 0} {bar.quantity === 1 ? 'barra' : 'barre'} da {bar.length || 0}mm
                                            </div>
                                        ))}
                                        <div className="pt-2 mt-2 border-t border-gray-300 font-medium">
                                            Totale: {customBars.reduce((sum, bar) => sum + (bar.quantity || 0), 0)} barre
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between">
                                <button
                                    onClick={() => setStep(2)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Indietro
                                </button>
                                <button
                                    onClick={() => {
                                        // Validate that we have at least one bar with valid data
                                        const invalidBars = customBars.filter(bar =>
                                            bar.length === 3000 || bar.length === 6000 || bar.length > 6000
                                        );

                                        if (invalidBars.length > 0) {
                                            alert('Errore: Non puoi inserire barre di esattamente 3000mm, 6000mm o maggiori di 6000mm');
                                            return;
                                        }

                                        const validBars = customBars.filter(bar => bar.quantity > 0 && bar.length > 0);
                                        if (validBars.length > 0) {
                                            setCustomBars(validBars);
                                            setStep(3); // Go directly to finish selection (skip quantity step)
                                        } else {
                                            alert('Aggiungi almeno una barra con quantità e lunghezza valide');
                                        }
                                    }}
                                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                                    disabled={customBars.length === 0}
                                >
                                    Avanti
                                </button>
                            </div>
                        </div>
                    </div>
                );

            case 6:
                // Final review for "Tagliato a Misura"
                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">Riepilogo Finale</h2>

                        <div className="bg-gray-50 p-4 rounded-lg mb-6 max-w-2xl mx-auto">
                            <h3 className="font-medium mb-4">Prodotti da Aggiungere</h3>
                            {generateProductCodes().map((product, index) => (
                                <div key={index} className={`text-sm mb-2 p-2 bg-white rounded ${product.category === 'automatic' ? 'text-orange-600' : ''}`}>
                                    <div className="font-medium">{product.code}</div>
                                    <div>Quantità: {product.quantity}</div>
                                    <div>{product.description}</div>
                                    {product.category === 'automatic' && (
                                        <span className="text-xs bg-orange-100 px-2 py-1 rounded mt-1 inline-block">Aggiunta Automatica</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-center space-x-4 mt-6">
                            <button
                                onClick={() => setStep(3)}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Indietro
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Aggiungi al Carrello
                            </button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="space-y-6">
            {renderStep()}
            <RALModal
                isOpen={showRALModal}
                onClose={handleRALClose}
                onConfirm={handleRALConfirm}
                finishType={pendingFinishCode}
            />
        </div>
    );
};

export default LedProfileSelector;
